<script>
	import { onMount, onDestroy } from 'svelte';
	import { getApiUrl, getDivisionTitle, injectGlobalStyles, loadJson, normalizeCollection, playerUrl, teamUrl } from './shared.js';
	import WidgetSelect from './WidgetSelect.svelte';

	export let projectId = '';
	export let tag = 'mlbl';

	let host;
	let divisions = [];
	let selectedLeagueId = '';
	let selectedLeagueTag = '';
	let players = [];
	let loading = false;
	let loadingDivisions = false;
	let errorMessage = '';
	let syncingFromGlobal = false;

	const LEAGUE_CHANGED_EVENT = 'ilb:league-changed';

	$: leagueOptions = buildLeagueOptions(divisions);

	function getLeagueSelectionStore() {
		const globalScope = window;
		if (!globalScope.__ilbSelectedLeagueByProject) {
			globalScope.__ilbSelectedLeagueByProject = {};
		}
		return globalScope.__ilbSelectedLeagueByProject;
	}

	function getStoredLeagueId() {
		if (!projectId) return '';
		return getLeagueSelectionStore()[projectId] || '';
	}

	function rememberLeagueId(leagueId) {
		if (!projectId || !leagueId) return;
		getLeagueSelectionStore()[projectId] = leagueId;
	}

	function publishLeagueSelection(leagueId) {
		if (!projectId || !leagueId) return;
		window.dispatchEvent(new CustomEvent(LEAGUE_CHANGED_EVENT, { detail: { projectId, leagueId } }));
	}

	function handleGlobalLeagueChange(event) {
		const payload = event?.detail;
		if (!payload || payload.projectId !== projectId) return;
		if (!payload.leagueId || payload.leagueId === selectedLeagueId) return;
		if (!leagueOptions.some((item) => item.value === payload.leagueId)) return;
		syncingFromGlobal = true;
		selectedLeagueId = payload.leagueId;
	}

	function buildLeagueOptions(items) {
		return (items || []).map((division, index) => ({
			value: division?.id || division?._id || division?.providerId || `division-${index}`,
			label: getDivisionTitle(division)
		}));
	}

	function getLeagueTag(league) {
		return league?.tag || league?.Tag || '';
	}

	function getSelectedLeague() {
		return divisions.find((division) => {
			const id = division?.id || division?._id || division?.providerId || '';
			return id && String(id) === String(selectedLeagueId);
		}) || null;
	}

	async function loadDivisions() {
		if (!projectId) { divisions = []; return; }
		loadingDivisions = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/stats/project/${encodeURIComponent(projectId)}/leagues`;
			let raw = normalizeCollection(await loadJson(url));
			divisions = raw;
			const options = buildLeagueOptions(divisions);
			const stored = getStoredLeagueId();
			if (stored && options.some((o)=>o.value===stored)) {
				selectedLeagueId = stored;
			} else if (!selectedLeagueId || !options.some((o)=>o.value===selectedLeagueId)) {
				selectedLeagueId = options[0]?.value || '';
			}
		} catch(err) {
			console.error('Load divisions error', err);
			errorMessage = 'Ошибка загрузки дивизионов';
		} finally {
			loadingDivisions = false;
		}
	}

	// react to selection
	$: if (selectedLeagueId && !loadingDivisions) {
		rememberLeagueId(selectedLeagueId);
		if (!syncingFromGlobal) publishLeagueSelection(selectedLeagueId);
		else syncingFromGlobal = false;
		loadBestFive(selectedLeagueId);
	}

	async function loadBestFive(leagueId) {
		if (!leagueId) { players = []; return; }
		selectedLeagueTag = getLeagueTag(getSelectedLeague());
		loading = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/stats/competition/${encodeURIComponent(leagueId)}/best-five`;
			players = normalizeCollection(await loadJson(url));
		} catch (err) {
			console.error('Load best five error', err);
			errorMessage = 'Ошибка загрузки пятерки';
			players = [];
		} finally {
			loading = false;
		}
	}

	function findByPosition(pos) {
		return (players || []).find(p => (p.position || p.Position || '').toLowerCase() === pos.toLowerCase());
	}

	function posMeta(position) {
		switch(position) {
			case 'PointGuard': return { abbr: 'PG', cls: '--tl' };
			case 'Guard': return { abbr: 'SG', cls: '--tr' };
			case 'Center': return { abbr: 'C', cls: '--c' };
			case 'Forward': return { abbr: 'SF', cls: '--bl' };
			case 'PowerForward': return { abbr: 'PF', cls: '--br' };
			default: return null;
		}
	}

	function goToPlayer(p) {
		const personId = p.player?.providerId || p.Player?.providerId || p.Player?.ProviderId;
		const teamId = p.team?.providerId || p.Team?.providerId || p.Team?.ProviderId;
		if (!personId) return;
		window.location.href = playerUrl(personId, { tag: selectedLeagueTag || tag, teamId, compId: selectedLeagueId });
	}

	function goToTeam(p) {
		const teamId = p.team?.providerId || p.Team?.providerId || p.Team?.ProviderId;
		if (!teamId) return;
		window.location.href = teamUrl(teamId, { tag: selectedLeagueTag || tag, compId: selectedLeagueId });
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) injectGlobalStyles(root);
		window.addEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
		loadDivisions();
	});

	onDestroy(() => {
		window.removeEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
	});
</script>

<section class="lineup" bind:this={host} data-project-id={projectId}>
	<div class="container">
		<div class="headline" data-fade="bottom">
			<h2>Пятерка</h2>

			{#if loadingDivisions}
				<select disabled>
					<option>Загрузка...</option>
				</select>
			{:else if errorMessage}
				<div class="widget-status" style="color: red;">{errorMessage}</div>
			{:else}
				<WidgetSelect name="lineup-league" options={leagueOptions} bind:value={selectedLeagueId} className="--black" />
			{/if}
		</div>

		<div class="lineup__wrap" data-fade="bottom" data-delay="100">
			<div class="lineup__court" data-filter-content>
				{#if loading}
					<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
				{:else}
					{#each ['PointGuard','Guard','Center','Forward','PowerForward'] as pos}
						{#if findByPosition(pos)}
							{@const p = findByPosition(pos)}
							{@const m = posMeta(pos)}
							<div class={`lineup__card ${m.cls}`}>
								<div class="lineup__avatar" on:click={() => goToPlayer(p)} on:keypress={() => goToPlayer(p)} role="button" tabindex="0" title="Открыть профиль игрока">
									<img src={p.player?.photo || p.Player?.photo || p.Player?.Photo} alt={`${p.player?.lastName || p.Player?.LastName} ${p.player?.firstName || p.Player?.FirstName}`} class="lineup__photo">
									<span class="lineup__position">{m.abbr}</span>
									<span class="lineup__logo" on:click|stopPropagation={() => goToTeam(p)} on:keypress|stopPropagation={() => goToTeam(p)} role="button" tabindex="0" title="Открыть страницу команды">
										<img src={p.team?.logo || p.Team?.logo || p.Team?.Logo} alt="Команда">
									</span>
								</div>
								<div class="lineup__name" on:click={() => goToPlayer(p)} on:keypress={() => goToPlayer(p)} role="button" tabindex="0">
									<span>{p.player?.lastName || p.Player?.LastName}</span>
									<span>{p.player?.firstName || p.Player?.FirstName}</span>
								</div>
								<div class="lineup__meta">
									<span>#{p.displayNumber || p.DisplayNumber || p.playerNumber || p.PlayerNumber}</span>
									<span class="lineup__kpd">КПД <b>{p.ranking || p.Ranking || (p.stats && p.stats.ranking) || (p.Stats && p.Stats.Ranking) || '-'}</b></span>
								</div>
							</div>
						{/if}
					{/each}
				{/if}
			</div>

			<picture>
				<img src="/Static/region/img/lineup/field.png" data-fade="bottom" data-delay="100" alt="Площадка" class="lineup__field" loading="lazy">
			</picture>
		</div>
	</div>
</section>

<style>
	:host { display:block; }
	.lineup__card { /* minimal spacing to keep layout */ }
	.lineup__avatar, .lineup__name, .lineup__logo { cursor: pointer; }
</style>